/**
 * History — R1 rewrite. Round history table wrapped in AppShell.
 * Preserves: GET /game/history?limit=50&offset=0
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ChevronRight, Ticket } from "lucide-react";
import { Toaster } from "sonner";

import api from "../lib/api";
import { extractError } from "../lib/formatError";
import AppShell from "../components/ui-r1/AppShell";
import { Card, CardBody } from "../components/ui-r1/Card";
import { Badge } from "../components/ui-r1/Badge";
import ChatWidget from "../components/ChatWidget";

const COLOR_HEX = { red: "#FF6B6B", black: "#F4EDE0", gold: "#FFCE55" };

// Text-style choice display labels for non-color games.
const TEXT_CHOICE_LABELS = {
  "sexy-tattoo": { tattoo: "TATTOO", no_tattoo: "NO TATTOO" },
};

function isTextChoiceGame(slug) {
  return !!TEXT_CHOICE_LABELS[slug];
}

export default function History() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameFilter, setGameFilter] = useState("all");   // "all" | "bra-3-way" | "bra-2-way" | "sexy-tattoo" | "hot-bikini" | "scratch-card"

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const params = { limit: 50, offset: 0 };
        if (gameFilter !== "all") params.game_slug = gameFilter;
        const { data } = await api.get("/game/history", { params });
        setItems(data.items);
        setTotal(data.total);
      } catch (e) {
        setError(extractError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [gameFilter]);

  return (
    <AppShell activeSection="wallet" pageTitle="History">
      <Toaster theme="dark" position="top-center" />

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6" data-testid="history-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-1">Activity ledger</div>
            <h1 className="font-display italic text-3xl sm:text-4xl text-ink">Round history</h1>
            <p className="text-xs tracking-[0.3em] uppercase text-ink-2 mt-2" data-testid="history-count">
              {total} round{total === 1 ? "" : "s"} played
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={gameFilter}
              onChange={(e) => setGameFilter(e.target.value)}
              className="h-10 rounded-lg border border-line bg-elevated text-ink px-3 text-sm focus:border-pink focus:outline-none transition"
              data-testid="history-game-filter"
            >
              <option value="all">All games</option>
              <option value="bra-3-way">Bra 3-Way</option>
              <option value="bra-2-way">Bra 2-Way</option>
              <option value="sexy-tattoo">Sexy Tattoo</option>
              <option value="hot-bikini">Hot Bikini</option>
              <option value="scratch-card">Scratch Card</option>
            </select>
            <Link
              to="/verify"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-line text-ink-2 hover:text-ink hover:border-gold text-sm transition"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Open verifier <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {loading && <Card><CardBody className="text-sm text-ink-2">Loading…</CardBody></Card>}
        {error && (
          <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2 mb-4">
            {error}
          </div>
        )}

        {!loading && items.length === 0 && !error && (
          <Card data-testid="history-empty-state">
            <CardBody className="py-16 text-center">
              <div className="text-[10px] tracking-[0.35em] uppercase text-pink mb-3">No rounds yet</div>
              <p className="text-sm text-ink-2">
                Play your first round to see it here.{" "}
                <Link to="/game/bra-3-way" className="text-pink hover:underline">Play now →</Link>
              </p>
            </CardBody>
          </Card>
        )}

        {!loading && items.length > 0 && (
          <Card className="overflow-hidden" data-testid="history-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-[10px] tracking-widest uppercase text-ink-3 bg-highest/50">
                  <tr>
                    <Th>Date</Th>
                    <Th>Game</Th>
                    <Th>Model</Th>
                    <Th className="text-right">Bet</Th>
                    <Th>Pick</Th>
                    <Th>Correct</Th>
                    <Th>Result</Th>
                    <Th className="text-right">Payout</Th>
                    <Th>Seed hash</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.round_id} className="border-b border-line last:border-0 hover:bg-highest/30 transition" data-testid={`history-row-${r.round_id}`}>
                      <Td className="text-ink-3 text-xs whitespace-nowrap">{formatDate(r.created_at)}</Td>
                      <Td className="text-ink-2 text-xs whitespace-nowrap" data-testid={`history-row-game-${r.round_id}`}>{gameLabel(r.game_slug)}</Td>
                      <Td className="text-ink-2 font-mono text-xs">{r.model_id.replace("model_", "#")}</Td>
                      <Td className="text-right font-mono text-ink">${r.bet_amount.toFixed(2)}</Td>
                      <Td><ChoicePill gameSlug={r.game_slug} value={r.color_pick} /></Td>
                      <Td>
                        {r.game_slug === "scratch-card"
                          ? <ScratchMultiplierCell round={r} />
                          : r.correct_color
                          ? <ChoicePill gameSlug={r.game_slug} value={r.correct_color} />
                          : <span className="text-ink-3 text-xs italic">{r.status === "refunded" ? "hidden" : "—"}</span>}
                      </Td>
                      <Td><ResultBadge status={r.status} won={r.won} /></Td>
                      <Td className="text-right font-mono">
                        {r.won ? <span className="text-success">+${r.payout.toFixed(2)}</span>
                          : r.status === "refunded" ? <span className="text-ink-3">refund</span>
                          : <span className="text-ink-3">—</span>}
                      </Td>
                      <Td className="font-mono text-[10px] text-ink-3">{r.server_seed_hash.slice(0, 10)}…</Td>
                      <Td>
                        {r.server_seed ? (
                          <Link
                            to={`/verify?server_seed=${encodeURIComponent(r.server_seed)}&client_seed=${encodeURIComponent(r.client_seed)}&nonce=${r.nonce}&game_slug=${encodeURIComponent(r.game_slug || "bra-3-way")}`}
                            className="text-pink hover:underline text-[10px] uppercase tracking-widest whitespace-nowrap"
                            data-testid={`verify-link-${r.round_id}`}
                          >
                            Verify →
                          </Link>
                        ) : (
                          <span
                            className="text-ink-3 text-[10px] uppercase tracking-widest whitespace-nowrap cursor-help"
                            title="Rotate your seed on the game page to reveal the server seed and verify this round."
                            data-testid={`verify-locked-${r.round_id}`}
                          >
                            Rotate to verify
                          </span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      <ChatWidget />
    </AppShell>
  );
}

function Th({ children, className = "" }) { return <th className={`text-left px-4 py-3 font-normal ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }

function ColorSwatch({ color }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="w-3 h-3 rounded-full border border-line/60" style={{ background: COLOR_HEX[color] || "#888" }} />
      <span className="uppercase tracking-widest text-xs" style={{ color: COLOR_HEX[color] }}>{color}</span>
    </span>
  );
}

// Renders the round's pick / correct value using whichever visual style fits
// the game — colored swatch for color-based games, labelled text pill for
// text-based games (e.g. sexy-tattoo), and a "CARD" ticket pill for scratch
// rounds (which have no pick — the entire outcome is the surprise reveal).
function ChoicePill({ gameSlug, value }) {
  if (gameSlug === "scratch-card" || !value) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-semibold tracking-widest uppercase whitespace-nowrap"
        style={{
          borderColor: "rgba(255,206,85,0.45)",
          color: "#FFCE55",
          background: "rgba(255,206,85,0.07)",
        }}
        data-testid="history-scratch-pill"
      >
        <Ticket className="w-3 h-3" />
        Card
      </span>
    );
  }
  if (isTextChoiceGame(gameSlug)) {
    const label = TEXT_CHOICE_LABELS[gameSlug][value] || String(value || "").toUpperCase();
    const isYes = value === "tattoo";
    return (
      <span
        className="inline-flex items-center px-2 py-1 rounded-md border text-[10px] font-semibold tracking-widest uppercase whitespace-nowrap"
        style={{
          borderColor: isYes ? "rgba(255,59,122,0.55)" : "rgba(244,237,224,0.35)",
          color: isYes ? "#FF3B7A" : "#F4EDE0",
          background: isYes ? "rgba(255,59,122,0.08)" : "rgba(244,237,224,0.05)",
        }}
      >
        {label}
      </span>
    );
  }
  return <ColorSwatch color={value} />;
}

function ResultBadge({ status, won }) {
  if (status === "refunded") return <Badge variant="muted">Refunded</Badge>;
  if (status === "pending" || status === "resolving") return <Badge variant="gold">Pending</Badge>;
  if (status === "unscratched") return <Badge variant="gold">Unscratched</Badge>;
  if (won) return <Badge variant="success">Win</Badge>;
  return <Badge variant="danger">Loss</Badge>;
}

// Scratch rounds have no "correct pick" — the natural equivalent for the
// history row is the prize multiplier the card actually rolled. We derive it
// from payout / bet_amount (== card_price) because `RoundHistoryEntry` does
// not surface `payout_multiplier` explicitly. Unscratched cards show a dashed
// placeholder since the multiplier is still hidden until the player scratches.
function ScratchMultiplierCell({ round }) {
  if (round.status === "unscratched") {
    return <span className="text-ink-3 text-xs italic">hidden</span>;
  }
  const bet = Number(round.bet_amount) || 0;
  const payout = Number(round.payout) || 0;
  const mult = bet > 0 ? payout / bet : 0;
  if (mult <= 0) {
    return <span className="text-ink-3 text-xs italic">0×</span>;
  }
  // 4dp tolerance covers 1.5×, 3×, 10×, 50×, 200× cleanly. Trim trailing zeros.
  const label = (Math.round(mult * 100) / 100).toString();
  return (
    <span
      className="inline-flex items-center px-2 py-1 rounded-md border text-[10px] font-semibold tracking-widest uppercase whitespace-nowrap"
      style={{
        borderColor: "rgba(255,206,85,0.55)",
        color: "#FFCE55",
        background: "rgba(255,206,85,0.1)",
      }}
      data-testid={`history-scratch-mult-${round.round_id}`}
    >
      {label}×
    </span>
  );
}

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function gameLabel(slug) {
  if (slug === "bra-2-way") return "Bra 2-Way";
  if (slug === "sexy-tattoo") return "Sexy Tattoo";
  if (slug === "hot-bikini") return "Hot Bikini";
  if (slug === "scratch-card") return "Scratch Card";
  return "Bra 3-Way";
}
